import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { AdminUser, RoleType, ApprovalStatus, Integrator } from '../types';
import { adminApi } from '../api/admin';
import { integratorApi } from '../api/integrators';
import { useAuth } from '../context/AuthContext';
import IntegratorAutocomplete from '../components/IntegratorAutocomplete';
import './Machines.css';

const statusLabel: Record<ApprovalStatus, string> = {
  PENDING: 'Oczekuje',
  APPROVED: 'Zatwierdzony',
  REJECTED: 'Odrzucony',
};

const statusBadge: Record<ApprovalStatus, string> = {
  PENDING: 'badge-warning',
  APPROVED: 'badge-success',
  REJECTED: 'badge-danger',
};

const roleLabel: Record<RoleType, string> = {
  ADMIN: 'Administrator',
  EMPLOYEE_ADMIN: 'Admin integratora',
  EMPLOYEE: 'Pracownik',
  CUSTOMER: 'Klient',
};

const AdminUsers: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nipQuery, setNipQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<'ALL' | RoleType>('ALL');
  const [integrators, setIntegrators] = useState<Integrator[]>([]);
  const [integratorFilter, setIntegratorFilter] = useState<Integrator | null>(null);
  const [roleModalUser, setRoleModalUser] = useState<AdminUser | null>(null);
  const [roleModalValue, setRoleModalValue] = useState<RoleType>('EMPLOYEE');
  const [roleModalBusy, setRoleModalBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.listUsers();
      setUsers(data);
    } catch {
      setError('Nie udało się pobrać użytkowników');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    integratorApi.list()
      .then(({ data }) => setIntegrators(data))
      .catch(() => { /* filtr po firmie po prostu nie podpowie firm */ });
  }, []);

  const filteredUsers = useMemo(() => {
    const q = nipQuery.trim();
    return users.filter((u) => {
      if (q && !(u.customerNip || '').includes(q)) return false;
      if (roleFilter !== 'ALL' && !u.roles.includes(roleFilter)) return false;
      if (integratorFilter && u.integratorId !== integratorFilter.id) return false;
      return true;
    });
  }, [users, nipQuery, roleFilter, integratorFilter]);

  if (!user?.roles?.includes('ADMIN')) {
    return <Navigate to="/machines" replace />;
  }

  const approve = async (id: string) => {
    await adminApi.approve(id);
    load();
  };
  const reject = async (id: string) => {
    if (!window.confirm('Odrzucić tego użytkownika?')) return;
    await adminApi.reject(id);
    load();
  };
  const changeRole = async (id: string, roleType: RoleType) => {
    try {
      await adminApi.changeRole(id, { roleType });
      load();
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Nie udało się zmienić roli');
    }
  };

  const primaryRole = (u: AdminUser): RoleType | null => u.roles[0] ?? null;

  const openRoleModal = (u: AdminUser) => {
    setRoleModalUser(u);
    setRoleModalValue(primaryRole(u) || 'EMPLOYEE');
  };
  const closeRoleModal = () => {
    if (roleModalBusy) return;
    setRoleModalUser(null);
  };
  const submitRoleModal = async () => {
    if (!roleModalUser) return;
    setRoleModalBusy(true);
    try {
      await changeRole(roleModalUser.id, roleModalValue);
      setRoleModalUser(null);
    } finally {
      setRoleModalBusy(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Użytkownicy</h1>
      </div>

      {error && <div className="auth-error">{error}</div>}

      <div className="filters-bar">
        <div className="filter-group">
          <IntegratorAutocomplete
            integrators={integrators}
            value={integratorFilter}
            onChange={setIntegratorFilter}
            label="Firma (wpisz NIP lub nazwę)"
          />
        </div>
        <div className="filter-group">
          <label className="form-label">Szukaj po NIP klienta</label>
          <input
            className="form-input"
            type="text"
            value={nipQuery}
            onChange={(e) => setNipQuery(e.target.value)}
            placeholder="np. 1234567890"
          />
        </div>
        <div className="filter-group">
          <label className="form-label">Filtruj po roli</label>
          <select
            className="form-select"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as 'ALL' | RoleType)}
          >
            <option value="ALL">Wszystkie</option>
            <option value="EMPLOYEE">Pracownik</option>
            <option value="EMPLOYEE_ADMIN">Admin integratora</option>
            <option value="CUSTOMER">Klient</option>
            <option value="ADMIN">Administrator</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="empty-state">Ładowanie...</div>
      ) : users.length === 0 ? (
        <div className="empty-state">Brak użytkowników</div>
      ) : filteredUsers.length === 0 ? (
        <div className="empty-state">Brak użytkowników pasujących do filtrów</div>
      ) : (
        <div className="machines-grid" style={{ gridAutoRows: '1fr' }}>
          {filteredUsers.map((u) => {
            const role = primaryRole(u);
            const isCustomer = role === 'CUSTOMER';
            const isEmployee = role === 'EMPLOYEE';
            const isEmployeeAdmin = role === 'EMPLOYEE_ADMIN';
            let actions: React.ReactNode = null;
            if (u.approvalStatus === 'PENDING') {
              actions = (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn btn-primary btn-sm" onClick={() => approve(u.id)}>Zatwierdź</button>
                  <button className="btn btn-danger btn-sm" onClick={() => reject(u.id)}>Odrzuć</button>
                </div>
              );
            } else if (u.approvalStatus === 'REJECTED') {
              actions = (
                <button className="btn btn-primary btn-sm" onClick={() => approve(u.id)}>
                  Przywróć (zatwierdź)
                </button>
              );
            } else if (u.approvalStatus === 'APPROVED' && (isEmployee || isEmployeeAdmin) && !u.founderAdmin) {
              actions = (
                <button className="btn btn-primary btn-sm" onClick={() => openRoleModal(u)}>
                  Zmień rolę
                </button>
              );
            }
            const actionsSlot = actions ?? (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ visibility: 'hidden', pointerEvents: 'none' }}
                tabIndex={-1}
                aria-hidden="true"
              >
                Zmień rolę
              </button>
            );
            const showNip = isCustomer || !!u.customerNip;
            return (
              <div key={u.id} className="machine-card card" style={{ cursor: 'default', display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div className="machine-card-header" style={{ alignItems: 'flex-start' }}>
                  <div style={{
                    width: 40, height: 40, flexShrink: 0,
                    borderRadius: '50%', overflow: 'hidden',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-surface-alt)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-text-muted)',
                  }}>
                    {u.avatarUrl ? (
                      <img src={u.avatarUrl} alt="Avatar"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <span>{u.firstName?.[0]}{u.lastName?.[0]}</span>
                    )}
                  </div>
                  <h3 className="machine-card-name" style={{ flex: 1, minWidth: 0 }}>{u.firstName} {u.lastName}</h3>
                  <span className={`badge ${statusBadge[u.approvalStatus]}`}>
                    {statusLabel[u.approvalStatus]}
                  </span>
                </div>
                <div style={{
                  marginTop: 'auto',
                  paddingTop: '1rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                  gap: '0.75rem',
                }}>
                  <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', lineHeight: 1.55, minWidth: 0 }}>
                    <div><strong>E-mail:</strong> {u.username}</div>
                    <div><strong>Rola:</strong> {role ? roleLabel[role] : '—'}</div>
                    <div style={showNip ? undefined : { visibility: 'hidden' }}>
                      <strong>NIP firmy:</strong>{' '}
                      {showNip
                        ? (u.customerNip || <em style={{ color: 'var(--color-warning)' }}>brak — klient nie zobaczy żadnych maszyn</em>)
                        : 'placeholder'}
                    </div>
                  </div>
                  <div style={{ flexShrink: 0 }}>{actionsSlot}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {roleModalUser && (
        <div className="modal-overlay" onClick={closeRoleModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">
              Zmień rolę: {roleModalUser.firstName} {roleModalUser.lastName}
            </h2>
            <div className="form-group">
              <label className="form-label">Nowa rola</label>
              <select
                className="form-select"
                value={roleModalValue}
                onChange={(e) => setRoleModalValue(e.target.value as RoleType)}
                disabled={roleModalBusy}
              >
                <option value="EMPLOYEE">Pracownik</option>
                <option value="EMPLOYEE_ADMIN">Admin integratora</option>
                <option value="ADMIN">Administrator</option>
              </select>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeRoleModal}
                disabled={roleModalBusy}
              >
                Anuluj
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={submitRoleModal}
                disabled={roleModalBusy || roleModalValue === primaryRole(roleModalUser)}
              >
                {roleModalBusy ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
